import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalFullltaxFormComponent } from '../../../modals/other/modal-fullltax-form/modal-fullltax-form.component';
import { Blank, FeeType, Providers, RevenueTypeResponse } from '../../../../../core/models/information.model';
import { CityParkingCustomerInfoResponse } from '../../../../../core/models/customer.model';
import { InvoiceResponse } from '../../../../../core/models/invoice.model';
import { CityParkingAddressSelectResponse } from '../../../../../core/models/register-parking';
import { CreateFinanceManualPayload } from '../../../../../core/models/finance/finance-manual-list.model';
import { MapperFinanceManualListService } from '../../../../../core/mappers/finance/mapper-finance-manual-list.service';
import { ApiFinanceManualService } from '../../../../../core/services/api/finance/api-finance-manual.service';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { ApiMasterService } from '../../../../../core/services/api-master.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import {
  calculateVatLineAmounts,
  VatLineAmounts,
} from '../../../../../core/utils/vat-amount.util';

@Component({
  selector: 'app-revenue-add-form',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalFullltaxFormComponent],
  templateUrl: './revenue-add-form.component.html',
  styleUrl: './revenue-add-form.component.css',
})
export class RevenueAddFormComponent implements OnInit, OnChanges {
  @Input() siteId = 0;
  @Input() menuId = 0;
  @Output() cancel = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly siteState = inject(SiteStateService);
  private readonly apiFinanceManual = inject(ApiFinanceManualService);
  private readonly mapper = inject(MapperFinanceManualListService);
  private readonly apiFormService = inject(ApiFormService);
  private readonly apiMasterService = inject(ApiMasterService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  readonly vatSetting = inject(VatSettingService);

  locations: Providers[] = [];
  customers: CityParkingCustomerInfoResponse[] = [];
  revenueTypes: RevenueTypeResponse[] = [];
  paymentTypes: FeeType[] = [];
  banks: Blank[] = [];
  invoices: InvoiceResponse[] = [];
  addresses: CityParkingAddressSelectResponse[] = [];
  filteredRevenueTypes: RevenueTypeResponse[] = [];

  selectedLocationId: number | null = null;
  selectedCustomerId: number | null = null;
  selectedRevenueId: number | null = null;
  taxInvoiceNeeded = false;
  selectedTaxAddressId: number | null = null;
  selectedInvoiceId = 0;
  selectedPaymentTypeId: number | null = null;
  selectedBankId: number | null = null;
  description = '';
  feeAmount = 0;
  receiptImage = '';
  receiptImageName = '';
  delayed = false;
  dueDate = '';
  maxDate = '';
  userId = 0;
  revenueSearchTerm = '';
  receiptNo = '';

  isLoading = false;
  isFulltaxModalOpen = false;
  isConfirmModalOpen = false;
  openDropdown: string | null = null;

  get isSiteLocked(): boolean {
    return this.siteId > 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get resolvedMenuId(): number {
    return this.menuId || this.permissionService.getMenuId(this.router.url) || 0;
  }

  get resolvedSiteId(): number {
    return this.siteId || this.selectedLocationId || 0;
  }

  get locationOptions() {
    return this.locations.map((l) => ({ label: l.name, value: l.id }));
  }

  get customerOptions() {
    return this.customers.map((c) => ({ label: `${c.name} (${c.phone})`, value: c.id }));
  }

  get revenueOptions() {
    const list = this.revenueSearchTerm ? this.filteredRevenueTypes : this.revenueTypes;
    return list.map((r) => ({ label: r.name, value: r.id }));
  }

  get invoiceOptions() {
    const items = this.invoices.map((i) => ({
      label: i.invoiceNumber || String(i.id),
      value: i.id,
    }));
    return [{ label: '-- ไม่ใช้ Invoice --', value: 0 }, ...items];
  }

  get paymentTypeOptions() {
    return this.paymentTypes.map((p) => ({ label: p.name, value: p.id }));
  }

  get bankOptions() {
    return this.banks.map((b) => ({ label: `${b.name} (${b.accountNumber})`, value: b.id }));
  }

  get taxOptions() {
    return this.addresses.map((t) => ({ label: `${t.taxId} - ${t.name}`, value: t.id }));
  }

  get selectedRevenueType(): RevenueTypeResponse | undefined {
    if (!this.selectedRevenueId) return undefined;
    return this.revenueTypes.find((item) => item.id === this.selectedRevenueId);
  }

  get amountFieldHint(): string {
    const revenue = this.selectedRevenueType;
    return revenue?.vat ? 'จำนวนเงิน (รวม VAT)' : 'จำนวนเงิน (Amount)';
  }

  /** Staff always enter the final amount paid — VAT is extracted regardless of parking_fee.vat_included (matches API VatAmountHelper) */
  get amountPreview(): VatLineAmounts {
    const revenue = this.selectedRevenueType;
    return calculateVatLineAmounts(this.feeAmount, 1, revenue?.vat ?? false, true, this.vatSetting.ratePercent());
  }

  get confirmGrandTotal(): number {
    return this.amountPreview.total;
  }

  ngOnInit(): void {
    const userIdFromSession = sessionStorage.getItem('user_id');
    const userIdLocal = localStorage.getItem('userId');
    this.userId =
      Number(userIdLocal) ||
      Number(this.cookieService.get('user_id')) ||
      (userIdFromSession ? JSON.parse(userIdFromSession) : 0);

    const today = new Date().toISOString().split('T')[0];
    this.dueDate = today;
    this.maxDate = today;

    if (this.siteId > 0) {
      this.selectedLocationId = this.siteId;
    }

    this.loadMasterData();
  }

  loadMasterData(): void {
    this.isLoading = true;
    this.getProviders();
    this.getCityParkingCustomerList();
    this.getRevenueTypes();
    this.getPaymentTypes();

    if (this.selectedLocationId) {
      this.getBanks(this.selectedLocationId);
    }
  }

  toggleDropdown(dropdown: string, event: Event): void {
    if (dropdown === 'locations' && this.isSiteLocked) return;
    event.stopPropagation();
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  closeDropdowns(): void {
    this.openDropdown = null;
    this.revenueSearchTerm = '';
  }

  selectOption(dropdown: string, value: number | null): void {
    switch (dropdown) {
      case 'locations':
        this.selectedLocationId = value;
        this.onLocationChange();
        break;
      case 'customers':
        this.selectedCustomerId = value;
        this.onCustomerChange();
        break;
      case 'revenues':
        this.selectedRevenueId = value;
        this.revenueSearchTerm = '';
        break;
      case 'invoices':
        this.selectedInvoiceId = value ?? 0;
        break;
      case 'paymentTypes':
        this.selectedPaymentTypeId = value;
        this.onPaymentTypeChange();
        break;
      case 'banks':
        this.selectedBankId = value;
        break;
      case 'taxs':
        this.selectedTaxAddressId = value;
        break;
    }
    this.closeDropdowns();
  }

  getOptionName(dropdown: string, value: number | null): string {
    if (value === null || value === undefined) return '';
    switch (dropdown) {
      case 'locations':
        if (this.isSiteLocked) {
          return this.siteState.selectedSite()?.name || this.locations.find((l) => l.id === value)?.name || '';
        }
        return this.locations.find((l) => l.id === value)?.name || '';
      case 'customers': {
        const cust = this.customers.find((c) => c.id === value);
        return cust ? `${cust.name} (${cust.phone})` : '';
      }
      case 'revenues':
        return this.revenueTypes.find((r) => r.id === value)?.name || '';
      case 'invoices': {
        if (!value) return '-- ไม่ใช้ Invoice --';
        const inv = this.invoices.find((i) => i.id === value);
        return inv ? inv.invoiceNumber || String(inv.id) : '';
      }
      case 'paymentTypes':
        return this.paymentTypes.find((p) => p.id === value)?.name || '';
      case 'banks': {
        const bank = this.banks.find((b) => b.id === value);
        return bank ? `${bank.name} (${bank.accountNumber})` : '';
      }
      case 'taxs': {
        const tax = this.addresses.find((t) => t.id === value);
        return tax ? `${tax.taxId} - ${tax.name}` : '';
      }
      default:
        return '';
    }
  }

  onSearchRevenue(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.revenueSearchTerm = target.value;
    this.filterRevenueTypes();
  }

  filterRevenueTypes(): void {
    if (!this.revenueSearchTerm) {
      this.filteredRevenueTypes = this.revenueTypes;
      return;
    }
    const term = this.revenueSearchTerm.toLowerCase();
    this.filteredRevenueTypes = this.revenueTypes.filter((rev) => rev.name.toLowerCase().includes(term));
  }

  onLocationChange(): void {
    if (this.selectedLocationId) {
      this.getBanks(this.selectedLocationId);
    } else {
      this.banks = [];
    }
    this.getCityParkingCustomerList();
    this.loadInvoicesIfReady();
  }

  onCustomerChange(): void {
    if (this.selectedCustomerId) {
      this.getTaxAddresses(this.selectedCustomerId);
    } else {
      this.addresses = [];
      this.invoices = [];
      this.selectedInvoiceId = 0;
    }
    this.loadInvoicesIfReady();
  }

  loadInvoicesIfReady(): void {
    const siteId = this.resolvedSiteId;
    if (!siteId || !this.selectedCustomerId) {
      this.invoices = [];
      this.selectedInvoiceId = 0;
      return;
    }
    this.getInvoices(siteId, this.selectedCustomerId);
  }

  onPaymentTypeChange(): void {
    if (this.selectedPaymentTypeId?.toString() !== '3') {
      this.selectedBankId = null;
    }
  }

  openFulltaxModal(): void {
    if (!this.selectedCustomerId) {
      this.toast.error('กรุณาเลือกลูกค้าก่อน');
      return;
    }
    this.isFulltaxModalOpen = true;
  }

  closeFulltaxModal(): void {
    this.isFulltaxModalOpen = false;
  }

  saveFulltaxModal(): void {
    this.isFulltaxModalOpen = false;
    if (this.selectedCustomerId) {
      this.getTaxAddresses(this.selectedCustomerId);
    }
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('รองรับเฉพาะไฟล์รูปภาพ (PNG / JPG)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toast.error('ไฟล์รูปต้องมีขนาดไม่เกิน 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.receiptImage = String(e.target?.result ?? '');
      this.receiptImageName = file.name;
    };
    reader.readAsDataURL(file);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  submit(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (this.validateForm()) {
      this.isConfirmModalOpen = true;
    }
  }

  validateForm(): boolean {
    const siteId = this.resolvedSiteId;
    if (!siteId) {
      this.toast.error('กรุณาเลือกสถานที่');
      return false;
    }
    if (!this.selectedCustomerId) {
      this.toast.error('กรุณาเลือกลูกค้า');
      return false;
    }
    if (!this.selectedRevenueId) {
      this.toast.error('กรุณาเลือกประเภทรายได้');
      return false;
    }
    if (this.taxInvoiceNeeded && !this.selectedTaxAddressId) {
      this.toast.error('กรุณาเลือกที่อยู่ออกใบกำกับภาษี');
      return false;
    }
    if (!this.selectedPaymentTypeId) {
      this.toast.error('กรุณาเลือกประเภทการชำระเงิน');
      return false;
    }
    if (this.selectedPaymentTypeId?.toString() === '3' && !this.selectedBankId) {
      this.toast.error('กรุณาเลือกธนาคาร');
      return false;
    }
    if (this.feeAmount <= 0) {
      this.toast.error('กรุณาระบุจำนวนเงินที่ถูกต้อง');
      return false;
    }
    if (!this.receiptImage) {
      this.toast.error('กรุณาใส่รูปภาพใบเสร็จ');
      return false;
    }
    if (this.delayed) {
      if (!this.dueDate) {
        this.toast.error('กรุณาเลือกวันครบกำหนดชำระ');
        return false;
      }
      if (this.checkDueDateDelay() && !this.receiptNo) {
        this.toast.error('กรุณากรอกเลขที่ใบเสร็จ');
        return false;
      }
    }
    return true;
  }

  checkDueDateDelay(): boolean {
    const todayISO = new Date().toISOString().split('T')[0];
    const today = new Date(todayISO);
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today.getTime() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const due = new Date(this.dueDate);
    due.setHours(0, 0, 0, 0);
    return due < yesterday && this.delayed;
  }

  confirmSave(): void {
    if (!this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const menuId = this.resolvedMenuId;
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isConfirmModalOpen = false;
    this.isLoading = true;

    const payload: CreateFinanceManualPayload = {
      siteId: this.resolvedSiteId,
      cpCustomerId: this.selectedCustomerId || 0,
      fullTax: this.taxInvoiceNeeded,
      cpInvoiceId: this.selectedInvoiceId > 0 ? this.selectedInvoiceId : 0,
      cpAddressId: this.taxInvoiceNeeded ? this.selectedTaxAddressId || 0 : 0,
      userId: this.userId,
      menuId,
      feeId: this.selectedRevenueId || 0,
      feeTypeId: this.selectedPaymentTypeId || 0,
      bankId: this.selectedBankId || 0,
      amount: this.feeAmount,
      description: this.description,
      delayed: this.delayed,
      dueDate: this.dueDate,
      base64: this.receiptImage,
      receiptNo: this.receiptNo,
      receiptImageName: this.receiptImageName,
    };

    this.apiFinanceManual.createFinanceManual(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isLoading = false;
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกข้อมูลสำเร็จ');
          this.saved.emit();
        } else {
          this.toast.error(mapped.message || 'ไม่สามารถบันทึกข้อมูลได้');
        }
      },
      error: (err) => {
        console.error(err);
        this.toast.error(err?.error?.message || 'ไม่สามารถบันทึกข้อมูลได้');
        this.isLoading = false;
      },
    });
  }

  closeConfirmModal(): void {
    this.isConfirmModalOpen = false;
  }

  getProviders(): void {
    this.apiFormService.getProviders().subscribe({
      next: (res) => {
        this.locations = res;
        if (this.siteId > 0) {
          this.selectedLocationId = this.siteId;
        }
      },
      error: () => (this.isLoading = false),
    });
  }

  /**
   * โหลดลูกค้า (city_parking_customer) เฉพาะลานที่เลือก
   */
  getCityParkingCustomerList(): void {
    const seq = ++this.customerRequestSeq;
    const siteId = this.resolvedSiteId;
    if (!siteId) {
      this.customers = [];
      this.resetCustomerIfNotInSite();
      this.isLoading = false;
      return;
    }
    this.apiFormService.getCityParkingCustomerList(siteId).subscribe({
      next: (res) => {
        if (seq !== this.customerRequestSeq) return;
        this.customers = res ?? [];
        this.resetCustomerIfNotInSite();
        this.isLoading = false;
      },
      error: (err) => {
        if (seq !== this.customerRequestSeq) return;
        console.error(err);
        this.customers = [];
        this.toast.error('ไม่สามารถโหลดข้อมูลลูกค้าได้');
        this.isLoading = false;
      },
    });
  }

  getRevenueTypes(): void {
    this.apiFormService.getParkingFeeInfo().subscribe({
      next: (res) => {
        this.revenueTypes = (res ?? []).map((row) => this.normalizeRevenueType(row));
        this.filteredRevenueTypes = this.revenueTypes;
      },
      error: (err) => console.error(err),
    });
  }

  private normalizeRevenueType(row: RevenueTypeResponse | Record<string, unknown>): RevenueTypeResponse {
    const raw = row as Record<string, unknown>;
    return {
      id: Number(raw['id'] ?? raw['Id'] ?? 0),
      name: String(raw['name'] ?? raw['Name'] ?? ''),
      vat: Boolean(raw['vat'] ?? raw['Vat'] ?? false),
      vatIncluded: Boolean(raw['vatIncluded'] ?? raw['VatIncluded'] ?? false),
      fee: Number(raw['fee'] ?? raw['Fee'] ?? 0),
    };
  }

  getPaymentTypes(): void {
    this.apiFormService.getFeeType().subscribe({
      next: (res) => {
        this.paymentTypes = res;
      },
    });
  }

  getBanks(siteId: number): void {
    this.apiFormService.getBank(siteId).subscribe({
      next: (res) => {
        this.banks = res;
      },
    });
  }

  getTaxAddresses(customerId: number): void {
    this.apiFormService.getCityAddressForSelectByCustomerId(customerId).subscribe({
      next: (res) => {
        this.addresses = res;
        if (res.length > 0) {
          this.selectedTaxAddressId = res[0].id;
        }
      },
    });
  }

  getInvoices(siteId: number, customerId: number): void {
    this.apiMasterService.getInvoiceList(siteId, customerId).subscribe({
      next: (res) => {
        this.invoices = res;
        this.selectedInvoiceId = 0;
      },
      error: (err) => {
        console.error(err);
        this.invoices = [];
        this.selectedInvoiceId = 0;
        this.toast.error('ไม่สามารถโหลดข้อมูลใบแจ้งหนี้ได้');
      },
    });
  }

  private customerRequestSeq = 0;

  /** ลานจากแถบด้านข้างเปลี่ยนขณะฟอร์มเปิดอยู่ → โหลดลูกค้า/ธนาคารของลานใหม่ */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['siteId'];
    if (!change || change.firstChange) return;
    this.selectedLocationId = this.siteId > 0 ? this.siteId : null;
    this.onLocationChange();
  }

  /** ลูกค้าที่เลือกไว้ไม่อยู่ในลานปัจจุบัน → ล้างการเลือก */
  private resetCustomerIfNotInSite(): void {
    if (!this.selectedCustomerId) return;
    if (this.customers.some((c) => c.id === this.selectedCustomerId)) return;
    this.selectedCustomerId = null;
    this.selectedTaxAddressId = null;
    this.onCustomerChange();
  }
}
